import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { getLoanCampaign, evaluateProductLoan } from '../services/loanService';
import { getCategories } from '../services/categoryService';

const LoanContext = createContext({
  loanCampaign: null,
  categoryTree: [],
  loading: false,
  getLoanForProduct: () => null,
  refreshLoanCampaign: async () => null
});

export function LoanProvider({ children }) {
  const [loanCampaign, setLoanCampaign] = useState(null);
  const [categoryTree, setCategoryTree] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async (force = false) => {
    try {
      setLoading(true);
      const [campData, catData] = await Promise.all([
        getLoanCampaign(force).catch(() => null),
        getCategories().catch(() => [])
      ]);
      if (campData) {
        setLoanCampaign(campData);
      }
      if (Array.isArray(catData)) {
        setCategoryTree(catData);
      }
      return campData;
    } catch (err) {
      console.error('Failed to load loan campaign data:', err);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData(false);

    const handleUpdate = (e) => {
      if (e.detail?.campaign) {
        setLoanCampaign(e.detail.campaign);
      } else {
        fetchData(true);
      }
    };

    window.addEventListener('loanCampaignUpdated', handleUpdate);
    return () => {
      window.removeEventListener('loanCampaignUpdated', handleUpdate);
    };
  }, [fetchData]);

  const getLoanForProduct = useCallback((product) => {
    return evaluateProductLoan(product, loanCampaign, categoryTree);
  }, [loanCampaign, categoryTree]);

  const value = useMemo(() => ({
    loanCampaign,
    categoryTree,
    loading,
    getLoanForProduct,
    refreshLoanCampaign: () => fetchData(true)
  }), [loanCampaign, categoryTree, loading, getLoanForProduct, fetchData]);

  return (
    <LoanContext.Provider value={value}>
      {children}
    </LoanContext.Provider>
  );
}

export function useLoanCampaign() {
  return useContext(LoanContext);
}

export default LoanContext;
